import React from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
  Stack,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import { api } from '../services/api-client';
import { useAuth } from '../contexts/GoogleAuthContext';

// Shapes mirror ERS GET /api/v1/usage/status (proxied by the CC backend) and
// the settings/curation_ai.background_search control doc (#128).
interface ProviderUsage {
  provider: string;
  available: boolean;
  used?: number;
  allowance?: number;
  remaining?: number;
  pctUsed?: number;
  unit?: string; // "searches" | "credits" | "GB"
  periodEnd?: string;
  fetchedAt?: number | null; // epoch seconds the snapshot was last written
  ageSeconds?: number | null;
  stale?: boolean;
  lastAlert?: { level?: string; reason?: string } | null;
  source?: string; // "in_band_monitor" | "manual_refresh" | "watchdog"
  // Per-vendor places circuit-breaker state (searchapi/serpapi only; absent for decodo).
  circuit?: { isOpen?: boolean; secondsRemaining?: number | null } | null;
  // Spend circuit breaker (increment 1): $-budget fields, present only when the
  // provider has a configured unit price + budget ceiling (else the usage bar shows alone).
  spent?: number; // $ spent this period (used * unitPrice)
  remainingDollars?: number; // budgetCeiling - spent (may be negative on overage)
  pctBudget?: number; // 100 * spent / budgetCeiling (may exceed 100)
  unitPrice?: number; // $ per usage unit (the rate applied)
  budgetCeiling?: number; // $ authorized ceiling
}

interface UsageStatus {
  providers: ProviderUsage[];
  generatedAt?: number;
}

interface BackgroundSearchControl {
  enabled?: boolean;
  maxTermsPerRun?: number;
  minProviderHeadroomPct?: number;
}

function ageLabel(seconds?: number | null): string {
  if (seconds == null) return 'age unknown';
  if (seconds < 90) return `${Math.round(seconds)}s ago`;
  if (seconds < 5400) return `${Math.round(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h ago`;
  return `${Math.round(seconds / 86400)}d ago`;
}

// Absolute wall-clock from an epoch-SECONDS timestamp (snapshots store seconds).
function absTime(epochSeconds?: number | null): string {
  if (epochSeconds == null) return '—';
  return new Date(epochSeconds * 1000).toLocaleString();
}

// Whole counts (searches/credits) read as integers; bandwidth (GB) keeps decimals.
function fmtNum(n: number | undefined, unit?: string): string {
  const v = n ?? 0;
  if (unit === 'GB') return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return Math.round(v).toLocaleString();
}

// Dollar totals at 2dp; the per-unit rate keeps more precision (e.g. $0.005/request).
function fmtUsd(n?: number): string {
  return `$${(n ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function quotaColor(pct?: number): 'success' | 'warning' | 'error' {
  if (pct == null) return 'success';
  if (pct >= 90) return 'error';
  if (pct >= 70) return 'warning';
  return 'success';
}

// Monthly-quota providers "reset"; consumable/proxy plans (credits, GB) "expire".
function periodWord(unit?: string): string {
  return unit === 'GB' || unit === 'credits' ? 'expires' : 'resets';
}

export default function BackgroundSearch() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isDeveloper = user?.role === 'developer' || user?.role === 'admin';

  const {
    data: status,
    isLoading: statusLoading,
    isError: statusError,
    refetch,
    isFetching,
  } = useQuery<UsageStatus>('background-search-status', () => api.getBackgroundSearchStatus(), {
    staleTime: 30 * 1000,
  });

  // The ↻ button does a LIVE refresh: it asks the backend to re-poll each
  // vendor's free usage endpoint (so timestamps actually advance), then writes
  // the fresh result into the query cache. Falls back to a plain cache refetch
  // if the live poll fails (vendor down / timeout) so the panel never sticks.
  const [refreshing, setRefreshing] = React.useState(false);
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const fresh = await api.getBackgroundSearchStatus(true);
      queryClient.setQueryData('background-search-status', fresh);
    } catch (err) {
      console.warn('live usage refresh failed; falling back to cached refetch:', err);
      refetch();
    } finally {
      setRefreshing(false);
    }
  };

  // Spend circuit breaker — "Authorize next tranche" (developer-gated). One mutation
  // shared across providers; `authorize.variables` tracks which provider is in
  // flight. On success the status query is invalidated so the $ bar redraws against
  // the new ceiling. ERS owns the ratchet + hard cap (a 400 means already at cap).
  const authorize = useMutation((provider: string) => api.authorizeBudgetTranche(provider), {
    onSuccess: () => queryClient.invalidateQueries('background-search-status'),
  });

  // The curation_ai settings doc may not exist yet (seeding never enabled) —
  // a 404 just means "no control set", which reads as disabled (fail-closed).
  const { data: moduleConfig, isLoading: configLoading } = useQuery(
    ['module', 'curation_ai'],
    () => api.getModuleConfig('curation_ai'),
    {
      retry: false,
      useErrorBoundary: false,
      // Fail-closed: any error (incl. 404 when the doc doesn't exist yet) reads
      // as "no control set" -> disabled. Log non-404s for debuggability.
      onError: (err) => console.warn('curation_ai module config unavailable:', err),
    }
  );

  const control: BackgroundSearchControl = moduleConfig?.background_search ?? {};
  const enabled = control.enabled === true;

  const toggle = useMutation(
    (next: boolean) =>
      api.updateModuleConfig('curation_ai', { background_search: { enabled: next } }),
    { onSuccess: () => queryClient.invalidateQueries(['module', 'curation_ai']) }
  );

  if (statusLoading || configLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  const providers = status?.providers ?? [];

  return (
    <Box>
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}
      >
        <Box>
          <Typography variant="h5" gutterBottom>
            Background Search
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Automated venue-discovery seeding — kill-switch, budget, and live provider quota.
          </Typography>
        </Box>
        <Tooltip title="Refresh now (re-polls each vendor's free usage endpoint)">
          <span>
            <IconButton onClick={handleRefresh} disabled={refreshing || isFetching}>
              {refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Control: kill-switch + budget (writes settings/curation_ai.background_search) */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={enabled}
                  color="success"
                  disabled={!isDeveloper || toggle.isLoading}
                  onChange={(e) => toggle.mutate(e.target.checked)}
                />
              }
              label={
                <Typography variant="body1">Seeding {enabled ? 'enabled' : 'disabled'}</Typography>
              }
            />
            <Stack direction="row" spacing={3}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Max terms / run
                </Typography>
                <Typography variant="body2">{control.maxTermsPerRun ?? 50}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Min provider headroom
                </Typography>
                <Typography variant="body2">{control.minProviderHeadroomPct ?? 10}%</Typography>
              </Box>
            </Stack>
          </Stack>
          {!isDeveloper && (
            <Typography variant="caption" color="text.secondary">
              Developer role required to change the switch.
            </Typography>
          )}
          {toggle.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Failed to update the seeding switch.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* AI search tool usage (read-only, from ERS usage status) */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
        sx={{ mb: 1 }}
        flexWrap="wrap"
      >
        <Typography variant="subtitle1">AI Search Tool Usage</Typography>
        {status?.generatedAt != null && (
          <Typography variant="caption" color="text.secondary">
            Panel pulled {absTime(status.generatedAt)}
          </Typography>
        )}
      </Stack>
      {statusError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Could not reach the usage service — no live quota data.
        </Alert>
      )}
      <Grid container spacing={2}>
        {providers.map((p) => (
          <Grid item xs={12} sm={6} key={p.provider}>
            <Card variant="outlined">
              <CardContent>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <Typography variant="subtitle2" sx={{ textTransform: 'capitalize' }}>
                    {p.provider}
                  </Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {p.circuit && (
                      <Tooltip
                        title={
                          p.circuit.isOpen
                            ? 'Circuit OPEN — this vendor is being skipped (recent rate-limit / quota / error); it auto-recovers after the cooldown.'
                            : 'Circuit closed — vendor healthy.'
                        }
                      >
                        <Chip
                          size="small"
                          variant={p.circuit.isOpen ? 'filled' : 'outlined'}
                          color={p.circuit.isOpen ? 'error' : 'success'}
                          icon={p.circuit.isOpen ? <BlockIcon /> : <CheckCircleIcon />}
                          label={
                            p.circuit.isOpen
                              ? `circuit open${
                                  p.circuit.secondsRemaining
                                    ? ` · ${p.circuit.secondsRemaining}s`
                                    : ''
                                }`
                              : 'circuit ok'
                          }
                        />
                      </Tooltip>
                    )}
                    {p.available ? (
                      <Chip
                        size="small"
                        color={p.stale ? 'warning' : 'success'}
                        icon={p.stale ? <WarningIcon /> : <CheckCircleIcon />}
                        label={
                          p.stale ? `stale · ${ageLabel(p.ageSeconds)}` : ageLabel(p.ageSeconds)
                        }
                      />
                    ) : (
                      <Chip size="small" label="no data" />
                    )}
                  </Stack>
                </Stack>

                {p.available ? (
                  <>
                    {(() => {
                      const unit = p.unit ?? 'searches';
                      const suffix = unit === 'searches' ? '' : ` ${unit}`;
                      const over = (p.pctUsed ?? 0) > 100;
                      return (
                        <>
                          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              {fmtNum(p.used, unit)} / {fmtNum(p.allowance, unit)}
                              {suffix}
                            </Typography>
                            <Typography
                              variant="caption"
                              fontWeight={500}
                              color={over || (p.pctUsed ?? 0) >= 90 ? 'error.main' : 'text.primary'}
                            >
                              {p.pctUsed != null ? `${p.pctUsed.toFixed(0)}%` : '—'}
                            </Typography>
                          </Stack>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(100, p.pctUsed ?? 0)}
                            color={quotaColor(p.pctUsed)}
                            sx={{ height: 8, borderRadius: 1 }}
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 0.5 }}
                          >
                            {over
                              ? `over quota by ${fmtNum((p.used ?? 0) - (p.allowance ?? 0), unit)}${suffix}`
                              : `${fmtNum(p.remaining, unit)}${suffix} left`}
                            {p.periodEnd
                              ? ` · ${periodWord(unit)} ${new Date(p.periodEnd).toLocaleDateString()}`
                              : ''}
                          </Typography>
                        </>
                      );
                    })()}
                    {/* Spend circuit breaker $ bar — only when a budget ceiling is set (opt-in). */}
                    {p.budgetCeiling != null &&
                      p.budgetCeiling > 0 &&
                      (() => {
                        const overBudget = (p.pctBudget ?? 0) > 100;
                        const rateLabel =
                          p.unitPrice != null
                            ? ` · $${p.unitPrice.toLocaleString(undefined, {
                                maximumFractionDigits: 5,
                              })}/${p.unit ?? 'unit'}`
                            : '';
                        return (
                          <Box sx={{ mt: 1.5 }}>
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              sx={{ mb: 0.5 }}
                            >
                              <Typography variant="caption" color="text.secondary">
                                {fmtUsd(p.spent)} / {fmtUsd(p.budgetCeiling)} budget
                              </Typography>
                              <Typography
                                variant="caption"
                                fontWeight={500}
                                color={
                                  overBudget || (p.pctBudget ?? 0) >= 90
                                    ? 'error.main'
                                    : 'text.primary'
                                }
                              >
                                {p.pctBudget != null ? `${p.pctBudget.toFixed(0)}%` : '—'}
                              </Typography>
                            </Stack>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(100, p.pctBudget ?? 0)}
                              color={quotaColor(p.pctBudget)}
                              sx={{ height: 8, borderRadius: 1 }}
                            />
                            <Typography
                              variant="caption"
                              color={overBudget ? 'error.main' : 'text.secondary'}
                              sx={{ display: 'block', mt: 0.5 }}
                            >
                              {overBudget
                                ? `OVER BUDGET by ${fmtUsd(Math.abs(p.remainingDollars ?? 0))}`
                                : `${fmtUsd(p.remainingDollars)} left`}
                              {rateLabel}
                            </Typography>
                            {/* No authorize button for Google — its cap is GCP-native (quota + billing budget), not ours to ratchet. */}
                            {isDeveloper && p.provider !== 'google' && (
                              <Box sx={{ mt: 1 }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  disabled={
                                    authorize.isLoading && authorize.variables === p.provider
                                  }
                                  onClick={() => authorize.mutate(p.provider)}
                                >
                                  {authorize.isLoading && authorize.variables === p.provider
                                    ? 'Authorizing…'
                                    : 'Authorize next tranche'}
                                </Button>
                                {authorize.isSuccess && authorize.variables === p.provider && (
                                  <Typography variant="caption" color="success.main" sx={{ ml: 1 }}>
                                    → {fmtUsd(authorize.data?.newCeiling)} (cap{' '}
                                    {fmtUsd(authorize.data?.hardCap)})
                                  </Typography>
                                )}
                                {authorize.isError && authorize.variables === p.provider && (
                                  <Typography variant="caption" color="error.main" sx={{ ml: 1 }}>
                                    {(authorize.error as Error)?.message || 'Authorization failed'}
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </Box>
                        );
                      })()}
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}
                    >
                      Last refreshed {absTime(p.fetchedAt)} · {ageLabel(p.ageSeconds)}
                    </Typography>
                    {p.lastAlert?.level && (
                      <Alert
                        severity={p.lastAlert.level === 'critical' ? 'error' : 'warning'}
                        sx={{ mt: 1, py: 0 }}
                      >
                        {p.lastAlert.reason || `${p.lastAlert.level} alert`}
                      </Alert>
                    )}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No usage snapshot yet (the monitor writes one when the provider is used).
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
        {providers.length === 0 && !statusError && (
          <Grid item xs={12}>
            <Alert severity="info">No provider usage data available yet.</Alert>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
